import { useGLTF } from '@react-three/drei'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js'
import {
  LEFT_FOREARM_SENSOR_WORLD_QUATERNION_INVERSE,
  RIGHT_FOREARM_SENSOR_WORLD_QUATERNION_INVERSE,
  getEffectiveSensorQuaternion,
} from '@/features/avatar/utils/sensorFrame'
import { useBleStore } from '@/state/bleStore'
import { useSensorStore } from '@/state/sensorStore'
import { useTelemetryStore } from '@/state/telemetryStore'
import { useUiStore } from '@/state/uiStore'
import type { SensorPacket, SensorPlacement } from '@/features/ble/types'

interface Props {
  url: string
  applyArmsDownPose?: boolean
}

const ARM_FRAME_SIZE = 0.14
const ANGLE_SMOOTHING_FACTOR = 0.08
const QUATERNION_SMOOTHING_FACTOR = 0.05
const SENSOR_HIT_SPHERE_RADIUS = 0.12
const SENSOR_SELECTED_COLOR = '#6fb1ff'
const SENSOR_SELECTED_OPACITY = 0.35
const SENSOR_TELEMETRY_INTERVAL_MS = 100
const TORSO_DOWN_AXIS = new THREE.Vector3(0, -1, 0)
type ArmSide = 'left' | 'right'
type DrivenJointKey = 'leftUpperArm' | 'rightUpperArm' | 'leftForearm' | 'rightForearm'
type FramePlacement = SensorPlacement

const LEFT_UPPER_ARM_BONE_ALIASES = new Set(
  [
    'upper_arm.L',
    'upper_arm_l',
    'upperarmL',
    'humerus.L',
    'humerus_l',
    'humerusL',
    'humerus_lv',
  ].map((name) => name.toLowerCase().replace(/[^a-z0-9]/g, '')),
)
const LEFT_ARM_FRAME_ROTATION = new THREE.Quaternion()
const RIGHT_ARM_FRAME_ROTATION = new THREE.Quaternion().setFromAxisAngle(
  new THREE.Vector3(0, 1, 0),
  Math.PI,
)
const RIGHT_UPPER_ARM_BONE_ALIASES = new Set(
  [
    'upper_arm.R',
    'upper_arm_r',
    'upperarmR',
    'humerus.R',
    'humerus_r',
    'humerusR',
    'humerus_rv',
  ].map((name) => name.toLowerCase().replace(/[^a-z0-9]/g, '')),
)
const LEFT_FOREARM_BONE_ALIASES = new Set(
  ['forearm.L', 'forearm_l', 'forearmL', 'lower_arm.L', 'lower_arm_l', 'ulna_l', 'radius_l'].map(
    (name) => name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const RIGHT_FOREARM_BONE_ALIASES = new Set(
  ['forearm.R', 'forearm_r', 'forearmR', 'lower_arm.R', 'lower_arm_r', 'ulna_r', 'radius_r'].map(
    (name) => name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const LEFT_HAND_BONE_ALIASES = new Set(
  ['hand.L', 'hand_l', 'handL', 'wrist.L', 'wrist_l', 'wristL'].map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const RIGHT_HAND_BONE_ALIASES = new Set(
  ['hand.R', 'hand_r', 'handR', 'wrist.R', 'wrist_r', 'wristR'].map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const LEFT_SHOULDER_YAW_BONE_ALIASES = new Set(
  ['ShoulderYaw.L', 'shoulder_yaw.L', 'shoulder_yaw_l'].map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const RIGHT_SHOULDER_YAW_BONE_ALIASES = new Set(
  ['ShoulderYaw.R', 'shoulder_yaw.R', 'shoulder_yaw_r'].map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const LEFT_SHOULDER_PITCH_BONE_ALIASES = new Set(
  ['ShoulderPitch.L', 'shoulder_pitch.L', 'shoulder_pitch_l'].map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const RIGHT_SHOULDER_PITCH_BONE_ALIASES = new Set(
  ['ShoulderPitch.R', 'shoulder_pitch.R', 'shoulder_pitch_r'].map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, ''),
  ),
)
const LEFT_ELBOW_BONE_ALIASES = new Set(
  ['elbow.L', 'elbow_l', 'elbowL'].map((name) => name.toLowerCase().replace(/[^a-z0-9]/g, '')),
)
const RIGHT_ELBOW_BONE_ALIASES = new Set(
  ['elbow.R', 'elbow_r', 'elbowR'].map((name) => name.toLowerCase().replace(/[^a-z0-9]/g, '')),
)
const LEFT_ROBOT_SHOULDER_YAW_AXIS = new THREE.Vector3(0, -1, 0)
const RIGHT_ROBOT_SHOULDER_YAW_AXIS = new THREE.Vector3(0, 1, 0)
const LEFT_ROBOT_SHOULDER_PITCH_AXIS = new THREE.Vector3(0, -1, 0)
const RIGHT_ROBOT_SHOULDER_PITCH_AXIS = new THREE.Vector3(0, -1, 0)
const ROBOT_ELBOW_FLEXION_AXIS = new THREE.Vector3(1, 0, 0)
const ROBOT_FOREARM_TWIST_AXIS = new THREE.Vector3(0, 1, 0)
const ROBOT_SHOULDER_YAW_SOLVE_AXIS = LEFT_ROBOT_SHOULDER_YAW_AXIS
const robotRelativeQuaternion = new THREE.Quaternion()
const robotTwistQuaternion = new THREE.Quaternion()
const robotSwingQuaternion = new THREE.Quaternion()
const robotAxisQuaternion = new THREE.Quaternion()
const robotLimbDirectionVector = new THREE.Vector3()
const robotBindDirectionVector = new THREE.Vector3()
const robotTargetDirectionVector = new THREE.Vector3()
const robotYawUndoneDirectionVector = new THREE.Vector3()
const robotPitchAxisVector = new THREE.Vector3()
const robotProjectedFromVector = new THREE.Vector3()
const robotProjectedToVector = new THREE.Vector3()
const robotCrossVector = new THREE.Vector3()
const robotWorldDeltaQuaternion = new THREE.Quaternion()
const robotForearmBindInverseQuaternion = new THREE.Quaternion()
const robotElbowFlexionQuaternion = new THREE.Quaternion()
const robotForearmTwistQuaternion = new THREE.Quaternion()
const robotForearmFlexionAxis = new THREE.Vector3()
const ROBOT_WORLD_DOWN = new THREE.Vector3(0, -1, 0)
const ROBOT_WORLD_LEFT_INWARD = new THREE.Vector3(0, 0, 1)
const ROBOT_WORLD_RIGHT_INWARD = new THREE.Vector3(0, 0, -1)
const robotCalibratedArmDirectionVector = new THREE.Vector3()
const robotCalibratedLogoDirectionVector = new THREE.Vector3()
const robotMainAxisCandidates = [
  new THREE.Vector3(1, 0, 0),
  new THREE.Vector3(-1, 0, 0),
  new THREE.Vector3(0, 1, 0),
  new THREE.Vector3(0, -1, 0),
  new THREE.Vector3(0, 0, 1),
  new THREE.Vector3(0, 0, -1),
]

type RobotLimbAngles = {
  pitch: number
  twist: number
  yaw: number
}

type RobotShoulderAngles = {
  pitch: number
  yaw: number
}

type RobotShoulderAngleSmoothing = Record<'left' | 'right', RobotShoulderAngles | null>
type RobotShoulderMainAxisCalibration = Record<'left' | 'right', THREE.Vector3 | null>
type RobotShoulderLogoAxisCalibration = Record<'left' | 'right', THREE.Vector3 | null>

type RobotForearmAngles = {
  flexion: number
  twist: number
}

type RobotForearmAngleSmoothing = Record<'left' | 'right', RobotForearmAngles | null>

function normalizeBoneName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

function isLeftUpperArmBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (LEFT_UPPER_ARM_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('upperarml') || normalized.includes('lefthumerus')
}

function isRightUpperArmBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (RIGHT_UPPER_ARM_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('upperarmr') || normalized.includes('righthumerus')
}

function isLeftForearmBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (LEFT_FOREARM_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('forearml') || normalized.includes('leftforearm')
}

function isRightForearmBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (RIGHT_FOREARM_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('forearmr') || normalized.includes('rightforearm')
}

function isLeftHandBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (LEFT_HAND_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('handl') || normalized.includes('lefthand')
}

function isRightHandBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (RIGHT_HAND_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('handr') || normalized.includes('righthand')
}

function isLeftHandEndBone(boneName: string): boolean {
  return normalizeBoneName(boneName).includes('handendl')
}

function isRightHandEndBone(boneName: string): boolean {
  return normalizeBoneName(boneName).includes('handendr')
}

function isLeftShoulderYawBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (LEFT_SHOULDER_YAW_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('shoulderyawl')
}

function isRightShoulderYawBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (RIGHT_SHOULDER_YAW_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('shoulderyawr')
}

function isLeftShoulderPitchBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (LEFT_SHOULDER_PITCH_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('shoulderpitchl')
}

function isRightShoulderPitchBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (RIGHT_SHOULDER_PITCH_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('shoulderpitchr')
}

function isLeftElbowBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (LEFT_ELBOW_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('elbowl')
}

function isRightElbowBone(boneName: string): boolean {
  const normalized = normalizeBoneName(boneName)
  if (RIGHT_ELBOW_BONE_ALIASES.has(normalized)) {
    return true
  }

  return normalized.includes('elbowr')
}

function isTrackedUpperArmBone(boneName: string, side: ArmSide): boolean {
  return side === 'right' ? isRightUpperArmBone(boneName) : isLeftUpperArmBone(boneName)
}

function isTrackedForearmBone(boneName: string, side: ArmSide): boolean {
  return side === 'right' ? isRightForearmBone(boneName) : isLeftForearmBone(boneName)
}

function isTrackedHandBone(boneName: string, side: ArmSide): boolean {
  return side === 'right' ? isRightHandBone(boneName) : isLeftHandBone(boneName)
}

function getPlacementSide(
  sensorPlacement: 'leftArm' | 'rightArm' | 'leftHand' | 'rightHand',
): ArmSide {
  return sensorPlacement === 'rightArm' || sensorPlacement === 'rightHand' ? 'right' : 'left'
}

function isHandPlacement(
  sensorPlacement: 'leftArm' | 'rightArm' | 'leftHand' | 'rightHand',
): boolean {
  return sensorPlacement === 'leftHand' || sensorPlacement === 'rightHand'
}

function getSensorFrameRotation(
  sensorPlacement: 'leftArm' | 'rightArm' | 'leftHand' | 'rightHand',
): THREE.Quaternion {
  return getPlacementSide(sensorPlacement) === 'right'
    ? RIGHT_ARM_FRAME_ROTATION
    : LEFT_ARM_FRAME_ROTATION
}

function getFrameBoneAndOffset(
  placement: FramePlacement,
  bones: {
    leftUpperArmBone: THREE.Bone | null
    rightUpperArmBone: THREE.Bone | null
    leftForearmBone: THREE.Bone | null
    rightForearmBone: THREE.Bone | null
    leftHandBone: THREE.Bone | null
    rightHandBone: THREE.Bone | null
  },
): { bone: THREE.Bone | null; offsetY: number } {
  if (placement === 'leftArm') return { bone: bones.leftUpperArmBone, offsetY: 0.15 }
  if (placement === 'rightArm') return { bone: bones.rightUpperArmBone, offsetY: 0.15 }
  if (placement === 'leftHand') return { bone: bones.leftHandBone, offsetY: -0.02 }
  return { bone: bones.rightHandBone, offsetY: -0.02 }
}

function radiansToDegrees(radians: number): number {
  return (radians * 180) / Math.PI
}

function getBoneWorldDirection(bone: THREE.Bone | null): THREE.Vector3 | null {
  if (!bone) {
    return null
  }

  return new THREE.Vector3(0, 1, 0)
    .applyQuaternion(bone.getWorldQuaternion(new THREE.Quaternion()))
    .normalize()
}

function buildWorldTargetQuaternion(
  bindWorldQuaternion: THREE.Quaternion,
  packet: { orientation: { x: number; y: number; z: number; w: number } },
  frameCorrection: boolean,
  inverse: THREE.Quaternion,
  output: THREE.Quaternion,
  armFrameRotation?: THREE.Quaternion,
): THREE.Quaternion {
  const effective = getEffectiveSensorQuaternion(packet.orientation, frameCorrection)
  const worldDelta = effective.multiply(inverse)

  if (armFrameRotation) {
    // The art mannequin's arm bones are skinned to their local +X axis instead
    // of the +Y axis the shared sensor frame assumes. Conjugate the world delta
    // into the mannequin's arm frame and pre-multiply it into the bind pose so
    // flexion/extension/abduction stay on the correct anatomical axes.
    return output
      .copy(armFrameRotation)
      .multiply(worldDelta)
      .multiply(armFrameRotation)
      .multiply(bindWorldQuaternion)
  }

  return output.copy(bindWorldQuaternion).multiply(worldDelta)
}

function applySmoothedLocalQuaternion(
  key: DrivenJointKey,
  smoothing: Record<DrivenJointKey, THREE.Quaternion | null>,
  targetLocalQuaternion: THREE.Quaternion,
): THREE.Quaternion {
  if (!smoothing[key]) {
    smoothing[key] = targetLocalQuaternion.clone()
    return smoothing[key] as THREE.Quaternion
  }

  smoothing[key]!.slerp(targetLocalQuaternion, QUATERNION_SMOOTHING_FACTOR)
  return smoothing[key] as THREE.Quaternion
}

function resolveLocalQuaternionFromWorldTarget(
  bone: THREE.Object3D,
  targetWorldQuaternion: THREE.Quaternion,
  parentWorldQuaternion: THREE.Quaternion,
  parentWorldQuaternionInverse: THREE.Quaternion,
  targetLocalQuaternion: THREE.Quaternion,
): THREE.Quaternion {
  if (!(bone.parent instanceof THREE.Object3D)) {
    return targetLocalQuaternion.copy(targetWorldQuaternion)
  }

  bone.parent.getWorldQuaternion(parentWorldQuaternion)
  parentWorldQuaternionInverse.copy(parentWorldQuaternion).invert()
  return targetLocalQuaternion.copy(parentWorldQuaternionInverse).multiply(targetWorldQuaternion)
}

function normalizeRadians(angle: number): number {
  return THREE.MathUtils.euclideanModulo(angle + Math.PI, Math.PI * 2) - Math.PI
}

function getSignedTwistAngle(quaternion: THREE.Quaternion): number {
  const vectorLength = Math.hypot(quaternion.x, quaternion.y, quaternion.z)
  if (vectorLength <= Number.EPSILON) {
    return 0
  }

  const angle = 2 * Math.atan2(vectorLength, quaternion.w)
  const sign = Math.sign(quaternion.y)

  return normalizeRadians((sign || 1) * angle)
}

function smoothAngle(current: number, target: number, factor: number): number {
  return normalizeRadians(current + normalizeRadians(target - current) * factor)
}

function normalizeRobotShoulderAngles(angles: RobotShoulderAngles): RobotShoulderAngles {
  return {
    pitch: normalizeRadians(angles.pitch),
    yaw: normalizeRadians(angles.yaw),
  }
}

function applySmoothedRobotShoulderAngles(
  side: 'left' | 'right',
  smoothing: RobotShoulderAngleSmoothing,
  target: RobotShoulderAngles,
): RobotShoulderAngles {
  const current = smoothing[side]
  if (!current) {
    smoothing[side] = { ...target }
    return smoothing[side] as RobotShoulderAngles
  }

  current.pitch = smoothAngle(current.pitch, target.pitch, ANGLE_SMOOTHING_FACTOR)
  current.yaw = smoothAngle(current.yaw, target.yaw, ANGLE_SMOOTHING_FACTOR)
  return current
}

function detectRobotShoulderMainAxis(effectiveQuaternion: THREE.Quaternion): THREE.Vector3 {
  let bestCandidate = robotMainAxisCandidates[0]
  let bestDot = -Infinity

  robotMainAxisCandidates.forEach((candidate) => {
    const dot = robotProjectedFromVector
      .copy(candidate)
      .applyQuaternion(effectiveQuaternion)
      .dot(ROBOT_WORLD_DOWN)
    if (dot > bestDot) {
      bestDot = dot
      bestCandidate = candidate
    }
  })

  return bestCandidate.clone()
}

function detectRobotShoulderLogoAxis(
  side: ArmSide,
  effectiveQuaternion: THREE.Quaternion,
  calibratedMainAxis: THREE.Vector3,
): THREE.Vector3 {
  const inwardVector = side === 'right' ? ROBOT_WORLD_RIGHT_INWARD : ROBOT_WORLD_LEFT_INWARD
  let bestCandidate =
    robotMainAxisCandidates.find(
      (candidate) => Math.abs(candidate.dot(calibratedMainAxis)) < 1e-6,
    ) ?? robotMainAxisCandidates[0]
  let bestDot = -Infinity

  robotMainAxisCandidates.forEach((candidate) => {
    if (Math.abs(candidate.dot(calibratedMainAxis)) > 1e-6) {
      return
    }

    const dot = robotProjectedFromVector
      .copy(candidate)
      .applyQuaternion(effectiveQuaternion)
      .dot(inwardVector)
    if (dot > bestDot) {
      bestDot = dot
      bestCandidate = candidate
    }
  })

  return bestCandidate.clone()
}

function getRobotShoulderAnglesFromOrientation(
  side: ArmSide,
  calibratedMainAxis: THREE.Vector3,
  calibratedLogoAxis: THREE.Vector3,
  orientation: { x: number; y: number; z: number; w: number },
  frameCorrection: boolean,
): RobotShoulderAngles {
  const effectiveQuaternion = getEffectiveSensorQuaternion(orientation, frameCorrection)
  const inwardVector = side === 'right' ? ROBOT_WORLD_RIGHT_INWARD : ROBOT_WORLD_LEFT_INWARD
  const outwardVector = robotProjectedToVector.copy(inwardVector).negate()

  robotCalibratedArmDirectionVector
    .copy(calibratedMainAxis)
    .applyQuaternion(effectiveQuaternion)
    .normalize()

  robotCalibratedLogoDirectionVector
    .copy(calibratedLogoAxis)
    .applyQuaternion(effectiveQuaternion)
    .normalize()

  const pitch = normalizeRadians(
    Math.atan2(
      robotCalibratedArmDirectionVector.dot(outwardVector),
      robotCalibratedLogoDirectionVector.dot(inwardVector),
    ),
  )

  const armPlanarLength = Math.hypot(
    robotCalibratedArmDirectionVector.x,
    robotCalibratedArmDirectionVector.y,
  )
  const logoPlanarLength = Math.hypot(
    robotCalibratedLogoDirectionVector.x,
    robotCalibratedLogoDirectionVector.y,
  )

  const yawSource =
    logoPlanarLength > armPlanarLength
      ? robotCalibratedLogoDirectionVector
      : robotCalibratedArmDirectionVector

  const yaw = normalizeRadians(Math.atan2(yawSource.x, -yawSource.y))

  return normalizeRobotShoulderAngles({
    pitch,
    yaw,
  })
}

function getSignedAngleOnPlane(
  from: THREE.Vector3,
  to: THREE.Vector3,
  axis: THREE.Vector3,
): number {
  robotProjectedFromVector.copy(from)
  robotProjectedFromVector.addScaledVector(axis, -robotProjectedFromVector.dot(axis))

  robotProjectedToVector.copy(to)
  robotProjectedToVector.addScaledVector(axis, -robotProjectedToVector.dot(axis))

  if (
    robotProjectedFromVector.lengthSq() <= Number.EPSILON ||
    robotProjectedToVector.lengthSq() <= Number.EPSILON
  ) {
    return 0
  }

  robotProjectedFromVector.normalize()
  robotProjectedToVector.normalize()
  robotCrossVector.crossVectors(robotProjectedFromVector, robotProjectedToVector)

  return Math.atan2(
    axis.dot(robotCrossVector),
    robotProjectedFromVector.dot(robotProjectedToVector),
  )
}

function getRobotLimbAngles(
  bindQuaternion: THREE.Quaternion,
  targetQuaternion: THREE.Quaternion,
  parentBindQuaternion: THREE.Quaternion,
  limbLocalDirection: THREE.Vector3,
): RobotLimbAngles {
  robotRelativeQuaternion.copy(bindQuaternion).invert().multiply(targetQuaternion).normalize()

  // Keep twist extraction available for later forearm work, but ignore it for shoulder control.
  const ry = robotRelativeQuaternion.y
  const rw = robotRelativeQuaternion.w
  robotTwistQuaternion.set(0, ry, 0, rw)
  if (robotTwistQuaternion.lengthSq() <= Number.EPSILON) {
    robotTwistQuaternion.identity()
  } else {
    robotTwistQuaternion.normalize()
  }
  const twist = getSignedTwistAngle(robotTwistQuaternion)

  robotLimbDirectionVector.copy(limbLocalDirection).normalize()

  // Express the bind and target arm directions in ShoulderYaw space.
  robotBindDirectionVector
    .copy(robotLimbDirectionVector)
    .applyQuaternion(bindQuaternion)
    .applyQuaternion(parentBindQuaternion)
    .normalize()

  robotTargetDirectionVector
    .copy(robotLimbDirectionVector)
    .applyQuaternion(targetQuaternion)
    .applyQuaternion(parentBindQuaternion)
    .normalize()

  // First motor: flexion/extension around the chest-to-shoulder axis.
  const yaw = getSignedAngleOnPlane(
    robotBindDirectionVector,
    robotTargetDirectionVector,
    ROBOT_SHOULDER_YAW_SOLVE_AXIS,
  )

  // Undo the first motor so the second motor sees only the remaining swing.
  robotSwingQuaternion.setFromAxisAngle(ROBOT_SHOULDER_YAW_SOLVE_AXIS, -yaw)
  robotYawUndoneDirectionVector
    .copy(robotTargetDirectionVector)
    .applyQuaternion(robotSwingQuaternion)

  // Second motor axis is perpendicular to the yaw axis and the bind arm direction.
  robotPitchAxisVector.crossVectors(ROBOT_SHOULDER_YAW_SOLVE_AXIS, robotBindDirectionVector)
  if (robotPitchAxisVector.lengthSq() <= Number.EPSILON) {
    return { pitch: 0, twist, yaw }
  }

  robotPitchAxisVector.normalize()
  const pitch = getSignedAngleOnPlane(
    robotBindDirectionVector,
    robotYawUndoneDirectionVector,
    robotPitchAxisVector,
  )

  return {
    pitch: normalizeRadians(pitch),
    twist,
    yaw: normalizeRadians(yaw),
  }
}

function applyRobotShoulderPose(
  side: ArmSide,
  shoulderYawBone: THREE.Bone,
  shoulderPitchBone: THREE.Bone,
  elbowBone: THREE.Bone,
  upperArmBone: THREE.Bone,
  shoulderYawBindQuaternion: THREE.Quaternion,
  shoulderPitchBindQuaternion: THREE.Quaternion,
  upperArmBindQuaternion: THREE.Quaternion,
  targetUpperArmQuaternion: THREE.Quaternion,
  shoulderAngles?: RobotShoulderAngles,
) {
  const { pitch, yaw } =
    shoulderAngles ??
    getRobotLimbAngles(
      upperArmBindQuaternion,
      targetUpperArmQuaternion,
      shoulderPitchBindQuaternion,
      elbowBone.position,
    )

  const shoulderYawAxis =
    side === 'right' ? RIGHT_ROBOT_SHOULDER_YAW_AXIS : LEFT_ROBOT_SHOULDER_YAW_AXIS
  const shoulderPitchAxis =
    side === 'right' ? RIGHT_ROBOT_SHOULDER_PITCH_AXIS : LEFT_ROBOT_SHOULDER_PITCH_AXIS

  // ShoulderYaw: flexion/extension around the chest-to-shoulder axis.
  shoulderYawBone.quaternion
    .copy(shoulderYawBindQuaternion)
    .multiply(robotAxisQuaternion.setFromAxisAngle(shoulderYawAxis, yaw))

  // ShoulderPitch: abduction/adduction around the rig's fixed second shoulder motor axis.
  shoulderPitchBone.quaternion
    .copy(shoulderPitchBindQuaternion)
    .multiply(robotAxisQuaternion.setFromAxisAngle(shoulderPitchAxis, pitch))

  upperArmBone.quaternion.copy(upperArmBindQuaternion)
}

function getRobotForearmAngles(
  forearmBindQuaternion: THREE.Quaternion,
  bindWorldQuaternion: THREE.Quaternion,
  targetWorldQuaternion: THREE.Quaternion,
): RobotForearmAngles {
  // Sensor-relative rotation, expressed in the forearm bone's local frame.
  robotWorldDeltaQuaternion
    .copy(bindWorldQuaternion)
    .invert()
    .multiply(targetWorldQuaternion)
    .normalize()

  // Pronation/supination: rotation around the forearm's long axis (+Y).
  const twist = normalizeRadians(
    2 * Math.atan2(robotWorldDeltaQuaternion.y, robotWorldDeltaQuaternion.w),
  )
  robotForearmTwistQuaternion.setFromAxisAngle(ROBOT_FOREARM_TWIST_AXIS, twist)

  // Remove the twist to isolate the swing (flexion + sideways).
  robotElbowFlexionQuaternion
    .copy(robotForearmTwistQuaternion)
    .invert()
    .multiply(robotWorldDeltaQuaternion)
    .normalize()

  // Elbow hinge axis, expressed in the forearm bone's local frame.
  robotForearmBindInverseQuaternion.copy(forearmBindQuaternion).invert()
  robotForearmFlexionAxis
    .copy(ROBOT_ELBOW_FLEXION_AXIS)
    .applyQuaternion(robotForearmBindInverseQuaternion)
    .normalize()

  // Flexion/extension: the swing component around the elbow hinge. The
  // sideways component (orthogonal to both axes) is intentionally ignored.
  // Negate to match the sensor's flexion sign convention.
  const flexion = -normalizeRadians(
    2 *
      Math.atan2(
        robotForearmFlexionAxis.x * robotElbowFlexionQuaternion.x +
          robotForearmFlexionAxis.y * robotElbowFlexionQuaternion.y +
          robotForearmFlexionAxis.z * robotElbowFlexionQuaternion.z,
        robotElbowFlexionQuaternion.w,
      ),
  )

  return { flexion, twist }
}

function applySmoothedRobotForearmAngles(
  side: 'left' | 'right',
  smoothing: RobotForearmAngleSmoothing,
  target: RobotForearmAngles,
): RobotForearmAngles {
  const current = smoothing[side]
  if (!current) {
    smoothing[side] = { flexion: target.flexion, twist: target.twist }
    return smoothing[side] as RobotForearmAngles
  }

  current.flexion = smoothAngle(current.flexion, target.flexion, ANGLE_SMOOTHING_FACTOR)
  current.twist = smoothAngle(current.twist, target.twist, ANGLE_SMOOTHING_FACTOR)
  return current
}

function applyRobotForearmPose(
  elbowBone: THREE.Bone,
  forearmBone: THREE.Bone,
  elbowBindQuaternion: THREE.Quaternion,
  forearmBindQuaternion: THREE.Quaternion,
  forearmAngles: RobotForearmAngles,
) {
  elbowBone.quaternion
    .copy(elbowBindQuaternion)
    .multiply(robotAxisQuaternion.setFromAxisAngle(ROBOT_ELBOW_FLEXION_AXIS, forearmAngles.flexion))

  forearmBone.quaternion
    .copy(forearmBindQuaternion)
    .multiply(robotAxisQuaternion.setFromAxisAngle(ROBOT_FOREARM_TWIST_AXIS, forearmAngles.twist))
}

// The art mannequin's mesh is authored in a T-pose with its arms along the
// world ±Z axis, while its armature rest pose already has the arm bones down.
// Rotating the bones would drag the elbow/wrist joints out of place (which
// breaks forearm tracking), so instead we re-bind each arm bone's skinning to
// rotate the mesh arm down about its shoulder while the joints stay put.
const LEFT_ARM_DOWN_WORLD_ROTATION = new THREE.Quaternion().setFromAxisAngle(
  new THREE.Vector3(-1, 0, 0),
  Math.PI / 2,
)
const RIGHT_ARM_DOWN_WORLD_ROTATION = new THREE.Quaternion().setFromAxisAngle(
  new THREE.Vector3(1, 0, 0),
  Math.PI / 2,
)

// Sensor-frame rotations that align the shared sensor world frame with the
// mannequin's arm bones (which are skinned to their local +X axis instead of
// the +Y axis the shared sensor constants assume).
const MANNEQUIN_LEFT_ARM_FRAME_ROTATION = new THREE.Quaternion().setFromAxisAngle(
  new THREE.Vector3(0, 0, 1),
  Math.PI,
)
const MANNEQUIN_RIGHT_ARM_FRAME_ROTATION = new THREE.Quaternion().setFromAxisAngle(
  new THREE.Vector3(1, 0, 0),
  Math.PI,
)

// The mannequin's spine sits slightly off the world origin, so its left shoulder
// lands on the vertical axis while the torso is pushed to one side. Shift the
// whole model so the spine (main body) is centered on the vertical axis.
const MANNEQUIN_BODY_CENTER_OFFSET = new THREE.Vector3(0.02, 0, -0.103)

const armDownShoulderPosition = new THREE.Vector3()
const armDownPivotTranslation = new THREE.Matrix4()
const armDownPivotTranslationInverse = new THREE.Matrix4()
const armDownRotationMatrix = new THREE.Matrix4()
const armDownCorrection = new THREE.Matrix4()
const armDownBindWorld = new THREE.Matrix4()
const armDownNewInverse = new THREE.Matrix4()

function isArmBoneOnSide(boneName: string, side: ArmSide): boolean {
  if (side === 'left') {
    return (
      isLeftUpperArmBone(boneName) ||
      isLeftForearmBone(boneName) ||
      isLeftHandBone(boneName) ||
      isLeftHandEndBone(boneName)
    )
  }

  return (
    isRightUpperArmBone(boneName) ||
    isRightForearmBone(boneName) ||
    isRightHandBone(boneName) ||
    isRightHandEndBone(boneName)
  )
}

function applyArmsDownRestPose(scene: THREE.Object3D): void {
  scene.updateMatrixWorld(true)

  const skinnedMesh = scene.getObjectByProperty('isSkinnedMesh', true) as
    | THREE.SkinnedMesh
    | undefined
  const skeleton = skinnedMesh?.skeleton
  if (!skeleton || skeleton.bones.length !== skeleton.boneInverses.length) return

  let leftShoulder: THREE.Bone | null = null
  let rightShoulder: THREE.Bone | null = null
  skeleton.bones.forEach((bone) => {
    if (!leftShoulder && isLeftUpperArmBone(bone.name)) leftShoulder = bone
    if (!rightShoulder && isRightUpperArmBone(bone.name)) rightShoulder = bone
  })

  const reBindArm = (shoulder: THREE.Bone, rotation: THREE.Quaternion, side: ArmSide) => {
    shoulder.getWorldPosition(armDownShoulderPosition)
    armDownPivotTranslation.makeTranslation(
      armDownShoulderPosition.x,
      armDownShoulderPosition.y,
      armDownShoulderPosition.z,
    )
    armDownPivotTranslationInverse.makeTranslation(
      -armDownShoulderPosition.x,
      -armDownShoulderPosition.y,
      -armDownShoulderPosition.z,
    )
    armDownRotationMatrix.makeRotationFromQuaternion(rotation)
    armDownCorrection
      .copy(armDownPivotTranslation)
      .multiply(armDownRotationMatrix)
      .multiply(armDownPivotTranslationInverse)

    skeleton.bones.forEach((bone, index) => {
      if (!isArmBoneOnSide(bone.name, side)) return
      armDownBindWorld.copy(bone.matrixWorld)
      armDownNewInverse.copy(armDownBindWorld).invert().multiply(armDownCorrection)
      skeleton.boneInverses[index].copy(armDownNewInverse)
    })
  }

  if (leftShoulder) reBindArm(leftShoulder, LEFT_ARM_DOWN_WORLD_ROTATION, 'left')
  if (rightShoulder) reBindArm(rightShoulder, RIGHT_ARM_DOWN_WORLD_ROTATION, 'right')
}

/**
 * SkeletonModel loads and renders a rigged GLB model.
 * Must be wrapped in <Suspense> by the parent.
 */
export function SkeletonModel({ url, applyArmsDownPose = false }: Props) {
  const { scene: sourceScene } = useGLTF(url)
  const scene = useMemo(() => {
    const clonedScene = clone(sourceScene)
    if (applyArmsDownPose) {
      applyArmsDownRestPose(clonedScene)
      clonedScene.position.copy(MANNEQUIN_BODY_CENTER_OFFSET)
    }
    return clonedScene
  }, [sourceScene, applyArmsDownPose])
  const frameRefs = useRef<Map<FramePlacement, THREE.AxesHelper>>(new Map())
  const hitSphereRefs = useRef<Map<FramePlacement, THREE.Mesh>>(new Map())
  const telemetryLastUpdateRef = useRef(0)
  const leftShoulderYawBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightShoulderYawBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftShoulderPitchBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightShoulderPitchBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftUpperArmBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightUpperArmBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftElbowBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightElbowBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftForearmBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightForearmBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftHandBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightHandBindQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftUpperArmBindWorldQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightUpperArmBindWorldQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const leftForearmBindWorldQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const rightForearmBindWorldQuaternionRef = useRef<THREE.Quaternion | null>(null)
  const targetWorldQuaternionRef = useRef(new THREE.Quaternion())
  const parentWorldQuaternionRef = useRef(new THREE.Quaternion())
  const parentWorldQuaternionInverseRef = useRef(new THREE.Quaternion())
  const targetLocalQuaternionRef = useRef(new THREE.Quaternion())
  const smoothedLocalQuaternionRef = useRef(new THREE.Quaternion())
  const robotShoulderAngleSmoothingRef = useRef<RobotShoulderAngleSmoothing>({
    left: null,
    right: null,
  })
  const robotShoulderMainAxisCalibrationRef = useRef<RobotShoulderMainAxisCalibration>({
    left: null,
    right: null,
  })
  const robotShoulderLogoAxisCalibrationRef = useRef<RobotShoulderLogoAxisCalibration>({
    left: null,
    right: null,
  })
  const robotForearmAngleSmoothingRef = useRef<RobotForearmAngleSmoothing>({
    left: null,
    right: null,
  })
  const smoothingRef = useRef<Record<DrivenJointKey, THREE.Quaternion | null>>({
    leftUpperArm: null,
    rightUpperArm: null,
    leftForearm: null,
    rightForearm: null,
  })
  const frameCorrection = useUiStore((state) => state.frameCorrection)
  const sensorPlacement = useUiStore((state) => state.sensorPlacement)
  const selectedSensor = useUiStore((state) => state.selectedSensor)
  const side = useMemo(() => getPlacementSide(sensorPlacement), [sensorPlacement])
  const handPlacement = useMemo(() => isHandPlacement(sensorPlacement), [sensorPlacement])
  const { camera, gl } = useThree()

  const upperArmBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return

      if (!(node instanceof THREE.Bone)) return
      const bone = node

      if (isTrackedUpperArmBone(bone.name, side)) {
        matchedBone = bone
      }
    })

    return matchedBone
  }, [scene, side])

  const forearmBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return

      if (!(node instanceof THREE.Bone)) return
      const bone = node

      if (isTrackedForearmBone(bone.name, side)) {
        matchedBone = bone
      }
    })

    return matchedBone
  }, [scene, side])

  const handBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return

      if (!(node instanceof THREE.Bone)) return
      const bone = node

      if (isTrackedHandBone(bone.name, side)) {
        matchedBone = bone
      }
    })

    return matchedBone
  }, [scene, side])

  const leftUpperArmBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isTrackedUpperArmBone(node.name, 'left')) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const rightUpperArmBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isTrackedUpperArmBone(node.name, 'right')) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const leftShoulderYawBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isLeftShoulderYawBone(node.name)) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const rightShoulderYawBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isRightShoulderYawBone(node.name)) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const leftShoulderPitchBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isLeftShoulderPitchBone(node.name)) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const rightShoulderPitchBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isRightShoulderPitchBone(node.name)) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const leftElbowBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isLeftElbowBone(node.name)) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const rightElbowBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isRightElbowBone(node.name)) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const leftForearmBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isTrackedForearmBone(node.name, 'left')) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const rightForearmBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isTrackedForearmBone(node.name, 'right')) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const leftHandBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isTrackedHandBone(node.name, 'left')) matchedBone = node
    })

    return matchedBone
  }, [scene])

  const rightHandBone = useMemo<THREE.Bone | null>(() => {
    let matchedBone: THREE.Bone | null = null

    scene.traverse((node) => {
      if (matchedBone) return
      if (!(node instanceof THREE.Bone)) return
      if (isTrackedHandBone(node.name, 'right')) matchedBone = node
    })

    return matchedBone
  }, [scene])

  // Derive a stable string key that only changes when actual placements change.
  // Using the full `devices` map would fire on every Hz update (50-100 Hz) because
  // setDeviceHz creates a new object reference, which would reset smoothingRef on
  // every packet via the useEffect cleanup — killing all smoothing.
  // String primitives are compared by value so the selector only fires when
  // placements actually change.
  const activePlacementsKey = useBleStore((state) =>
    state.deviceOrder.map((id) => state.devices[id]?.placement ?? '').join(','),
  )
  const activePlacements = useMemo(() => {
    const seen = new Set<SensorPlacement>()
    return activePlacementsKey
      .split(',')
      .filter((p): p is SensorPlacement => p !== '')
      .filter((p) => {
        if (seen.has(p)) return false
        seen.add(p)
        return true
      })
  }, [activePlacementsKey])
  const hasLeftRobotRig = Boolean(leftShoulderYawBone && leftShoulderPitchBone && leftElbowBone)
  const hasRightRobotRig = Boolean(rightShoulderYawBone && rightShoulderPitchBone && rightElbowBone)

  useEffect(() => {
    if (!upperArmBone) {
      return
    }

    if (handPlacement && (!forearmBone || !handBone)) {
      return
    }

    leftShoulderYawBindQuaternionRef.current = leftShoulderYawBone?.quaternion.clone() ?? null
    rightShoulderYawBindQuaternionRef.current = rightShoulderYawBone?.quaternion.clone() ?? null
    leftShoulderPitchBindQuaternionRef.current = leftShoulderPitchBone?.quaternion.clone() ?? null
    rightShoulderPitchBindQuaternionRef.current = rightShoulderPitchBone?.quaternion.clone() ?? null
    leftUpperArmBindQuaternionRef.current = leftUpperArmBone?.quaternion.clone() ?? null
    rightUpperArmBindQuaternionRef.current = rightUpperArmBone?.quaternion.clone() ?? null
    leftElbowBindQuaternionRef.current = leftElbowBone?.quaternion.clone() ?? null
    rightElbowBindQuaternionRef.current = rightElbowBone?.quaternion.clone() ?? null
    leftForearmBindQuaternionRef.current = leftForearmBone?.quaternion.clone() ?? null
    rightForearmBindQuaternionRef.current = rightForearmBone?.quaternion.clone() ?? null
    leftHandBindQuaternionRef.current = leftHandBone?.quaternion.clone() ?? null
    rightHandBindQuaternionRef.current = rightHandBone?.quaternion.clone() ?? null

    scene.updateMatrixWorld(true)
    leftUpperArmBindWorldQuaternionRef.current = leftUpperArmBone
      ? leftUpperArmBone.getWorldQuaternion(new THREE.Quaternion())
      : null
    rightUpperArmBindWorldQuaternionRef.current = rightUpperArmBone
      ? rightUpperArmBone.getWorldQuaternion(new THREE.Quaternion())
      : null
    leftForearmBindWorldQuaternionRef.current = leftForearmBone
      ? leftForearmBone.getWorldQuaternion(new THREE.Quaternion())
      : null
    rightForearmBindWorldQuaternionRef.current = rightForearmBone
      ? rightForearmBone.getWorldQuaternion(new THREE.Quaternion())
      : null

    const nextFrames = new Map<FramePlacement, THREE.AxesHelper>()
    const nextSpheres = new Map<FramePlacement, THREE.Mesh>()

    activePlacements.forEach((placement) => {
      const { bone, offsetY } = getFrameBoneAndOffset(placement, {
        leftUpperArmBone,
        rightUpperArmBone,
        leftForearmBone,
        rightForearmBone,
        leftHandBone,
        rightHandBone,
      })

      if (!bone) {
        return
      }

      const existingFrame = frameRefs.current.get(placement)
      const frame = existingFrame ?? new THREE.AxesHelper(ARM_FRAME_SIZE)
      frame.position.set(0, offsetY, 0)
      frame.quaternion.copy(getSensorFrameRotation(placement))
      bone.add(frame)
      nextFrames.set(placement, frame)

      if (applyArmsDownPose) {
        const existingSphere = hitSphereRefs.current.get(placement)
        const sphere =
          existingSphere ??
          new THREE.Mesh(
            new THREE.SphereGeometry(SENSOR_HIT_SPHERE_RADIUS, 16, 16),
            new THREE.MeshBasicMaterial({
              color: SENSOR_SELECTED_COLOR,
              transparent: true,
              opacity: 0,
              depthWrite: false,
            }),
          )
        sphere.position.set(0, offsetY, 0)
        sphere.quaternion.copy(getSensorFrameRotation(placement))
        sphere.userData.sensorPlacement = placement
        bone.add(sphere)
        nextSpheres.set(placement, sphere)
      }
    })

    frameRefs.current.forEach((frame, placement) => {
      if (nextFrames.has(placement)) return
      frame.parent?.remove(frame)
      frame.geometry.dispose()
      const frameMaterial = frame.material
      if (Array.isArray(frameMaterial)) {
        frameMaterial.forEach((material) => material.dispose())
      } else {
        frameMaterial.dispose()
      }
    })

    hitSphereRefs.current.forEach((sphere, placement) => {
      if (nextSpheres.has(placement)) return
      sphere.parent?.remove(sphere)
      sphere.geometry.dispose()
      const sphereMaterial = sphere.material
      if (Array.isArray(sphereMaterial)) {
        sphereMaterial.forEach((material) => material.dispose())
      } else {
        sphereMaterial.dispose()
      }
    })

    frameRefs.current = nextFrames
    hitSphereRefs.current = nextSpheres

    return () => {
      nextFrames.forEach((frame) => {
        frame.parent?.remove(frame)
        frame.geometry.dispose()
        const frameMaterial = frame.material
        if (Array.isArray(frameMaterial)) {
          frameMaterial.forEach((material) => material.dispose())
        } else {
          frameMaterial.dispose()
        }
      })

      nextSpheres.forEach((sphere) => {
        sphere.parent?.remove(sphere)
        sphere.geometry.dispose()
        const sphereMaterial = sphere.material
        if (Array.isArray(sphereMaterial)) {
          sphereMaterial.forEach((material) => material.dispose())
        } else {
          sphereMaterial.dispose()
        }
      })

      if (leftUpperArmBone && leftUpperArmBindQuaternionRef.current)
        leftUpperArmBone.quaternion.copy(leftUpperArmBindQuaternionRef.current)
      if (rightUpperArmBone && rightUpperArmBindQuaternionRef.current)
        rightUpperArmBone.quaternion.copy(rightUpperArmBindQuaternionRef.current)
      if (leftShoulderYawBone && leftShoulderYawBindQuaternionRef.current)
        leftShoulderYawBone.quaternion.copy(leftShoulderYawBindQuaternionRef.current)
      if (rightShoulderYawBone && rightShoulderYawBindQuaternionRef.current)
        rightShoulderYawBone.quaternion.copy(rightShoulderYawBindQuaternionRef.current)
      if (leftShoulderPitchBone && leftShoulderPitchBindQuaternionRef.current)
        leftShoulderPitchBone.quaternion.copy(leftShoulderPitchBindQuaternionRef.current)
      if (rightShoulderPitchBone && rightShoulderPitchBindQuaternionRef.current)
        rightShoulderPitchBone.quaternion.copy(rightShoulderPitchBindQuaternionRef.current)
      if (leftElbowBone && leftElbowBindQuaternionRef.current)
        leftElbowBone.quaternion.copy(leftElbowBindQuaternionRef.current)
      if (rightElbowBone && rightElbowBindQuaternionRef.current)
        rightElbowBone.quaternion.copy(rightElbowBindQuaternionRef.current)
      if (leftForearmBone && leftForearmBindQuaternionRef.current)
        leftForearmBone.quaternion.copy(leftForearmBindQuaternionRef.current)
      if (rightForearmBone && rightForearmBindQuaternionRef.current)
        rightForearmBone.quaternion.copy(rightForearmBindQuaternionRef.current)
      if (leftHandBone && leftHandBindQuaternionRef.current)
        leftHandBone.quaternion.copy(leftHandBindQuaternionRef.current)
      if (rightHandBone && rightHandBindQuaternionRef.current)
        rightHandBone.quaternion.copy(rightHandBindQuaternionRef.current)

      leftUpperArmBindQuaternionRef.current = null
      rightUpperArmBindQuaternionRef.current = null
      leftShoulderYawBindQuaternionRef.current = null
      rightShoulderYawBindQuaternionRef.current = null
      leftShoulderPitchBindQuaternionRef.current = null
      rightShoulderPitchBindQuaternionRef.current = null
      leftElbowBindQuaternionRef.current = null
      rightElbowBindQuaternionRef.current = null
      leftForearmBindQuaternionRef.current = null
      rightForearmBindQuaternionRef.current = null
      leftHandBindQuaternionRef.current = null
      rightHandBindQuaternionRef.current = null
      leftUpperArmBindWorldQuaternionRef.current = null
      rightUpperArmBindWorldQuaternionRef.current = null
      leftForearmBindWorldQuaternionRef.current = null
      rightForearmBindWorldQuaternionRef.current = null
      robotShoulderAngleSmoothingRef.current.left = null
      robotShoulderAngleSmoothingRef.current.right = null
      robotShoulderMainAxisCalibrationRef.current.left = null
      robotShoulderMainAxisCalibrationRef.current.right = null
      robotShoulderLogoAxisCalibrationRef.current.left = null
      robotShoulderLogoAxisCalibrationRef.current.right = null
      robotForearmAngleSmoothingRef.current.left = null
      robotForearmAngleSmoothingRef.current.right = null
      smoothingRef.current.leftUpperArm = null
      smoothingRef.current.rightUpperArm = null
      smoothingRef.current.leftForearm = null
      smoothingRef.current.rightForearm = null
    }
  }, [
    activePlacements,
    applyArmsDownPose,
    leftElbowBone,
    leftForearmBone,
    leftHandBone,
    leftShoulderPitchBone,
    leftShoulderYawBone,
    leftUpperArmBone,
    rightElbowBone,
    rightForearmBone,
    rightHandBone,
    rightShoulderPitchBone,
    rightShoulderYawBone,
    rightUpperArmBone,
    upperArmBone,
  ])

  const publishSensorTelemetry = useCallback(() => {
    scene.updateMatrixWorld(true)

    const leftUpperArmDir = getBoneWorldDirection(leftUpperArmBone)
    const rightUpperArmDir = getBoneWorldDirection(rightUpperArmBone)
    const store = useTelemetryStore.getState()

    activePlacements.forEach((placement) => {
      const { bone, offsetY } = getFrameBoneAndOffset(placement, {
        leftUpperArmBone,
        rightUpperArmBone,
        leftForearmBone,
        rightForearmBone,
        leftHandBone,
        rightHandBone,
      })
      if (!bone) return

      const previousDirection =
        placement === 'leftArm' || placement === 'rightArm'
          ? TORSO_DOWN_AXIS
          : placement === 'leftHand'
            ? leftUpperArmDir
            : rightUpperArmDir

      const position = new THREE.Vector3(0, offsetY, 0).applyMatrix4(bone.matrixWorld)
      const worldQuaternion = bone.getWorldQuaternion(new THREE.Quaternion())
      const segmentDirection = new THREE.Vector3(0, 1, 0)
        .applyQuaternion(worldQuaternion)
        .normalize()
      const euler = new THREE.Euler().setFromQuaternion(worldQuaternion, 'XYZ')

      const angleToPreviousLinkDeg = previousDirection
        ? radiansToDegrees(
            Math.acos(THREE.MathUtils.clamp(segmentDirection.dot(previousDirection), -1, 1)),
          )
        : 0
      const angleToHorizontalDeg = radiansToDegrees(
        Math.asin(THREE.MathUtils.clamp(Math.abs(segmentDirection.y), 0, 1)),
      )

      store.setTelemetry(placement, {
        position: { x: position.x, y: position.y, z: position.z },
        rollPitchYaw: {
          roll: radiansToDegrees(euler.x),
          pitch: radiansToDegrees(euler.y),
          yaw: radiansToDegrees(euler.z),
        },
        angleToPreviousLinkDeg,
        angleToHorizontalDeg,
      })
    })

    const activeSet = new Set(activePlacements)
    ;(Object.keys(store.telemetry) as SensorPlacement[]).forEach((placement) => {
      if (!activeSet.has(placement)) {
        store.clearTelemetry(placement)
      }
    })
  }, [
    scene,
    activePlacements,
    leftUpperArmBone,
    rightUpperArmBone,
    leftForearmBone,
    rightForearmBone,
    leftHandBone,
    rightHandBone,
  ])

  // Tint the hit sphere blue while its sensor is selected.
  useEffect(() => {
    hitSphereRefs.current.forEach((sphere, placement) => {
      const material = sphere.material as THREE.MeshBasicMaterial
      material.opacity = placement === selectedSensor ? SENSOR_SELECTED_OPACITY : 0
    })
  }, [selectedSensor])

  // Select / de-select a sensor by clicking its 3D frame.
  useEffect(() => {
    if (!applyArmsDownPose) return

    const canvas = gl.domElement
    const raycaster = new THREE.Raycaster()
    const pointer = new THREE.Vector2()
    let pointerDown: { x: number; y: number } | null = null

    const handlePointerDown = (event: PointerEvent) => {
      pointerDown = { x: event.clientX, y: event.clientY }
    }

    const handlePointerUp = (event: PointerEvent) => {
      if (!pointerDown) return
      const deltaX = event.clientX - pointerDown.x
      const deltaY = event.clientY - pointerDown.y
      pointerDown = null

      if (deltaX * deltaX + deltaY * deltaY > 36) return

      const rect = canvas.getBoundingClientRect()
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      )
      raycaster.setFromCamera(pointer, camera)

      const hits = raycaster.intersectObjects([...hitSphereRefs.current.values()], false)
      if (hits.length === 0) return

      const placement = hits[0].object.userData.sensorPlacement as FramePlacement | undefined
      if (!placement) return

      const { selectedSensor: current, setSelectedSensor } = useUiStore.getState()
      setSelectedSensor(current === placement ? null : placement)
    }

    canvas.addEventListener('pointerdown', handlePointerDown)
    canvas.addEventListener('pointerup', handlePointerUp)

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown)
      canvas.removeEventListener('pointerup', handlePointerUp)
    }
  }, [applyArmsDownPose, camera, gl])

  // Clear the selection when its sensor is no longer active.
  useEffect(() => {
    if (!selectedSensor || activePlacements.includes(selectedSensor)) return
    useUiStore.getState().setSelectedSensor(null)
  }, [activePlacements, selectedSensor])

  useFrame(() => {
    const byDevice = useSensorStore.getState().byDevice
    const { deviceOrder, devices } = useBleStore.getState()

    let leftArmPacket: SensorPacket | null = null
    let rightArmPacket: SensorPacket | null = null
    let leftHandPacket: SensorPacket | null = null
    let rightHandPacket: SensorPacket | null = null

    deviceOrder.forEach((deviceId) => {
      const managed = devices[deviceId]
      const packet = byDevice[deviceId]?.latestPacket
      if (!managed || !packet) return

      if (managed.placement === 'leftArm' && !leftArmPacket) leftArmPacket = packet
      if (managed.placement === 'rightArm' && !rightArmPacket) rightArmPacket = packet
      if (managed.placement === 'leftHand' && !leftHandPacket) leftHandPacket = packet
      if (managed.placement === 'rightHand' && !rightHandPacket) rightHandPacket = packet
    })

    const leftForearmDrivenPacket = leftHandPacket
    const rightForearmDrivenPacket = rightHandPacket

    if (
      leftUpperArmBone &&
      leftUpperArmBindQuaternionRef.current &&
      leftUpperArmBindWorldQuaternionRef.current
    ) {
      if (leftArmPacket) {
        buildWorldTargetQuaternion(
          leftUpperArmBindWorldQuaternionRef.current,
          leftArmPacket,
          frameCorrection,
          LEFT_FOREARM_SENSOR_WORLD_QUATERNION_INVERSE,
          targetWorldQuaternionRef.current,
          applyArmsDownPose ? MANNEQUIN_LEFT_ARM_FRAME_ROTATION : undefined,
        )
        resolveLocalQuaternionFromWorldTarget(
          leftUpperArmBone,
          targetWorldQuaternionRef.current,
          parentWorldQuaternionRef.current,
          parentWorldQuaternionInverseRef.current,
          targetLocalQuaternionRef.current,
        )
        const smoothedLocal = applySmoothedLocalQuaternion(
          'leftUpperArm',
          smoothingRef.current,
          targetLocalQuaternionRef.current,
        )
        smoothedLocalQuaternionRef.current.copy(smoothedLocal)
        if (
          hasLeftRobotRig &&
          leftShoulderYawBone &&
          leftUpperArmBindQuaternionRef.current &&
          leftShoulderYawBindQuaternionRef.current &&
          leftShoulderPitchBone &&
          leftShoulderPitchBindQuaternionRef.current &&
          leftElbowBone
        ) {
          const leftRobotShoulderSource = leftArmPacket as SensorPacket
          if (!robotShoulderMainAxisCalibrationRef.current.left) {
            robotShoulderMainAxisCalibrationRef.current.left = detectRobotShoulderMainAxis(
              getEffectiveSensorQuaternion(leftRobotShoulderSource.orientation, frameCorrection),
            )
          }
          if (!robotShoulderLogoAxisCalibrationRef.current.left) {
            robotShoulderLogoAxisCalibrationRef.current.left = detectRobotShoulderLogoAxis(
              'left',
              getEffectiveSensorQuaternion(leftRobotShoulderSource.orientation, frameCorrection),
              robotShoulderMainAxisCalibrationRef.current.left as THREE.Vector3,
            )
          }
          const leftRobotShoulderAngles = applySmoothedRobotShoulderAngles(
            'left',
            robotShoulderAngleSmoothingRef.current,
            getRobotShoulderAnglesFromOrientation(
              'left',
              robotShoulderMainAxisCalibrationRef.current.left as THREE.Vector3,
              robotShoulderLogoAxisCalibrationRef.current.left as THREE.Vector3,
              leftRobotShoulderSource.orientation,
              frameCorrection,
            ),
          )
          applyRobotShoulderPose(
            'left',
            leftShoulderYawBone,
            leftShoulderPitchBone,
            leftElbowBone,
            leftUpperArmBone,
            leftShoulderYawBindQuaternionRef.current,
            leftShoulderPitchBindQuaternionRef.current,
            leftUpperArmBindQuaternionRef.current,
            smoothedLocalQuaternionRef.current,
            leftRobotShoulderAngles,
          )
        } else {
          leftUpperArmBone.quaternion.copy(smoothedLocalQuaternionRef.current)
        }
      } else {
        leftUpperArmBone.quaternion.copy(leftUpperArmBindQuaternionRef.current)
        if (leftShoulderYawBone && leftShoulderYawBindQuaternionRef.current)
          leftShoulderYawBone.quaternion.copy(leftShoulderYawBindQuaternionRef.current)
        if (leftShoulderPitchBone && leftShoulderPitchBindQuaternionRef.current)
          leftShoulderPitchBone.quaternion.copy(leftShoulderPitchBindQuaternionRef.current)
        robotShoulderAngleSmoothingRef.current.left = null
        robotShoulderMainAxisCalibrationRef.current.left = null
        robotShoulderLogoAxisCalibrationRef.current.left = null
        smoothingRef.current.leftUpperArm = null
      }
    }

    if (
      rightUpperArmBone &&
      rightUpperArmBindQuaternionRef.current &&
      rightUpperArmBindWorldQuaternionRef.current
    ) {
      if (rightArmPacket) {
        buildWorldTargetQuaternion(
          rightUpperArmBindWorldQuaternionRef.current,
          rightArmPacket,
          frameCorrection,
          RIGHT_FOREARM_SENSOR_WORLD_QUATERNION_INVERSE,
          targetWorldQuaternionRef.current,
          applyArmsDownPose ? MANNEQUIN_RIGHT_ARM_FRAME_ROTATION : undefined,
        )
        resolveLocalQuaternionFromWorldTarget(
          rightUpperArmBone,
          targetWorldQuaternionRef.current,
          parentWorldQuaternionRef.current,
          parentWorldQuaternionInverseRef.current,
          targetLocalQuaternionRef.current,
        )
        const smoothedLocal = applySmoothedLocalQuaternion(
          'rightUpperArm',
          smoothingRef.current,
          targetLocalQuaternionRef.current,
        )
        smoothedLocalQuaternionRef.current.copy(smoothedLocal)
        if (
          hasRightRobotRig &&
          rightShoulderYawBone &&
          rightUpperArmBindQuaternionRef.current &&
          rightShoulderYawBindQuaternionRef.current &&
          rightShoulderPitchBone &&
          rightShoulderPitchBindQuaternionRef.current &&
          rightElbowBone
        ) {
          const rightRobotShoulderSource = rightArmPacket as SensorPacket
          if (!robotShoulderMainAxisCalibrationRef.current.right) {
            robotShoulderMainAxisCalibrationRef.current.right = detectRobotShoulderMainAxis(
              getEffectiveSensorQuaternion(rightRobotShoulderSource.orientation, frameCorrection),
            )
          }
          if (!robotShoulderLogoAxisCalibrationRef.current.right) {
            robotShoulderLogoAxisCalibrationRef.current.right = detectRobotShoulderLogoAxis(
              'right',
              getEffectiveSensorQuaternion(rightRobotShoulderSource.orientation, frameCorrection),
              robotShoulderMainAxisCalibrationRef.current.right as THREE.Vector3,
            )
          }
          const rightRobotShoulderAngles = applySmoothedRobotShoulderAngles(
            'right',
            robotShoulderAngleSmoothingRef.current,
            getRobotShoulderAnglesFromOrientation(
              'right',
              robotShoulderMainAxisCalibrationRef.current.right as THREE.Vector3,
              robotShoulderLogoAxisCalibrationRef.current.right as THREE.Vector3,
              rightRobotShoulderSource.orientation,
              frameCorrection,
            ),
          )
          applyRobotShoulderPose(
            'right',
            rightShoulderYawBone,
            rightShoulderPitchBone,
            rightElbowBone,
            rightUpperArmBone,
            rightShoulderYawBindQuaternionRef.current,
            rightShoulderPitchBindQuaternionRef.current,
            rightUpperArmBindQuaternionRef.current,
            smoothedLocalQuaternionRef.current,
            rightRobotShoulderAngles,
          )
        } else {
          rightUpperArmBone.quaternion.copy(smoothedLocalQuaternionRef.current)
        }
      } else {
        rightUpperArmBone.quaternion.copy(rightUpperArmBindQuaternionRef.current)
        if (rightShoulderYawBone && rightShoulderYawBindQuaternionRef.current)
          rightShoulderYawBone.quaternion.copy(rightShoulderYawBindQuaternionRef.current)
        if (rightShoulderPitchBone && rightShoulderPitchBindQuaternionRef.current)
          rightShoulderPitchBone.quaternion.copy(rightShoulderPitchBindQuaternionRef.current)
        robotShoulderAngleSmoothingRef.current.right = null
        robotShoulderMainAxisCalibrationRef.current.right = null
        robotShoulderLogoAxisCalibrationRef.current.right = null
        smoothingRef.current.rightUpperArm = null
      }
    }

    if (
      leftForearmBone &&
      leftForearmBindQuaternionRef.current &&
      leftForearmBindWorldQuaternionRef.current
    ) {
      if (leftForearmDrivenPacket) {
        buildWorldTargetQuaternion(
          leftForearmBindWorldQuaternionRef.current,
          leftForearmDrivenPacket,
          frameCorrection,
          LEFT_FOREARM_SENSOR_WORLD_QUATERNION_INVERSE,
          targetWorldQuaternionRef.current,
          applyArmsDownPose ? MANNEQUIN_LEFT_ARM_FRAME_ROTATION : undefined,
        )
        resolveLocalQuaternionFromWorldTarget(
          leftForearmBone,
          targetWorldQuaternionRef.current,
          parentWorldQuaternionRef.current,
          parentWorldQuaternionInverseRef.current,
          targetLocalQuaternionRef.current,
        )
        const smoothedLocal = applySmoothedLocalQuaternion(
          'leftForearm',
          smoothingRef.current,
          targetLocalQuaternionRef.current,
        )
        smoothedLocalQuaternionRef.current.copy(smoothedLocal)
        if (hasLeftRobotRig && leftElbowBone && leftElbowBindQuaternionRef.current) {
          const leftForearmAngles = applySmoothedRobotForearmAngles(
            'left',
            robotForearmAngleSmoothingRef.current,
            getRobotForearmAngles(
              leftForearmBindQuaternionRef.current,
              leftForearmBindWorldQuaternionRef.current,
              targetWorldQuaternionRef.current,
            ),
          )
          applyRobotForearmPose(
            leftElbowBone,
            leftForearmBone,
            leftElbowBindQuaternionRef.current,
            leftForearmBindQuaternionRef.current,
            leftForearmAngles,
          )
        } else {
          leftForearmBone.quaternion.copy(smoothedLocalQuaternionRef.current)
        }
      } else {
        leftForearmBone.quaternion.copy(leftForearmBindQuaternionRef.current)
        if (leftElbowBone && leftElbowBindQuaternionRef.current)
          leftElbowBone.quaternion.copy(leftElbowBindQuaternionRef.current)
        robotForearmAngleSmoothingRef.current.left = null
        smoothingRef.current.leftForearm = null
      }
    }

    if (
      rightForearmBone &&
      rightForearmBindQuaternionRef.current &&
      rightForearmBindWorldQuaternionRef.current
    ) {
      if (rightForearmDrivenPacket) {
        buildWorldTargetQuaternion(
          rightForearmBindWorldQuaternionRef.current,
          rightForearmDrivenPacket,
          frameCorrection,
          RIGHT_FOREARM_SENSOR_WORLD_QUATERNION_INVERSE,
          targetWorldQuaternionRef.current,
          applyArmsDownPose ? MANNEQUIN_RIGHT_ARM_FRAME_ROTATION : undefined,
        )
        resolveLocalQuaternionFromWorldTarget(
          rightForearmBone,
          targetWorldQuaternionRef.current,
          parentWorldQuaternionRef.current,
          parentWorldQuaternionInverseRef.current,
          targetLocalQuaternionRef.current,
        )
        const smoothedLocal = applySmoothedLocalQuaternion(
          'rightForearm',
          smoothingRef.current,
          targetLocalQuaternionRef.current,
        )
        smoothedLocalQuaternionRef.current.copy(smoothedLocal)
        if (hasRightRobotRig && rightElbowBone && rightElbowBindQuaternionRef.current) {
          const rightForearmAngles = applySmoothedRobotForearmAngles(
            'right',
            robotForearmAngleSmoothingRef.current,
            getRobotForearmAngles(
              rightForearmBindQuaternionRef.current,
              rightForearmBindWorldQuaternionRef.current,
              targetWorldQuaternionRef.current,
            ),
          )
          applyRobotForearmPose(
            rightElbowBone,
            rightForearmBone,
            rightElbowBindQuaternionRef.current,
            rightForearmBindQuaternionRef.current,
            rightForearmAngles,
          )
        } else {
          rightForearmBone.quaternion.copy(smoothedLocalQuaternionRef.current)
        }
      } else {
        rightForearmBone.quaternion.copy(rightForearmBindQuaternionRef.current)
        if (rightElbowBone && rightElbowBindQuaternionRef.current)
          rightElbowBone.quaternion.copy(rightElbowBindQuaternionRef.current)
        robotForearmAngleSmoothingRef.current.right = null
        smoothingRef.current.rightForearm = null
      }
    }

    if (applyArmsDownPose) {
      const now = performance.now()
      if (now - telemetryLastUpdateRef.current >= SENSOR_TELEMETRY_INTERVAL_MS) {
        telemetryLastUpdateRef.current = now
        publishSensorTelemetry()
      }
    }
  })

  return <primitive object={scene} />
}
